import React, { useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadMatterCD } from "../../action/matterCD";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import { LabelText } from "../Common/Typography/AppTypography";
import { selectMenuProps } from "../../utils/selectMenuProps";

const formControlSx = {
  width: "100%",
  maxWidth: { xs: "100%", md: 360 },
};

const parseProductIds = (value) =>
  (value || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

function UploadCDForm() {
  const allowedDeviceCategories =
    useSelector((state) => state.auth.allowedDeviceCategories) || [];
  const dispatch = useDispatch();
  const [productIds, setProductIds] = useState("");
  const [fwVersion, setFwVersion] = useState("");
  const [category, setCategory] = useState("");
  const [cdFile, setCdFile] = useState(null);
  const [fwVersionError, setFwVersionError] = useState("");
  const [loading, setLoading] = useState(false);
  const cdInputRef = useRef(null);
  const parsedProductIds = parseProductIds(productIds);

  const resetForm = () => {
    setProductIds("");
    setFwVersion("");
    setCategory("");
    setCdFile(null);
    setFwVersionError("");
    setLoading(false);
    if (cdInputRef.current) cdInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFwVersionError("");
    if (!category) {
      dispatch(setAlert("Category Cannot be Empty", "danger"));
      return;
    }
    if (!fwVersion?.trim()) {
      dispatch(setAlert("Firmware Version Cannot be Empty", "danger"));
      return;
    }
    if (!parsedProductIds.length) {
      dispatch(setAlert("Please enter at least one Product ID.", "danger"));
      return;
    }
    if (!cdFile) {
      dispatch(setAlert("Please select a CD file to upload.", "danger"));
      return;
    }
    if (!/^\d+\.\d+$/.test(fwVersion)) {
      setFwVersionError(
        "Firmware Version must be a decimal value (e.g. 1.0, 2.5)"
      );
      dispatch(
        setAlert(
          "Firmware Version must be a decimal value (e.g. 1.0, 2.5)",
          "danger"
        )
      );
      return;
    }
    setLoading(true);

    const formData = new FormData();
    formData.append("cd", cdFile);
    formData.append("category", category);
    formData.append("fwVersion", fwVersion);
    formData.append("productIds", parsedProductIds.join(","));
    try {
      await uploadMatterCD(formData);
      dispatch(setAlert("CDs uploaded successfully!", "success"));
      resetForm();
    } catch (err) {
      let errMsg = err.response?.data?.message || err.message;
      if (
        typeof errMsg === "string" &&
        errMsg.toLowerCase().includes("already exist")
      ) {
        errMsg = "A record with these details already exists.";
      }
      dispatch(setAlert(errMsg, "danger"));
    }
    setLoading(false);
  };

  return (
    <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
      <Alert />
      <form onSubmit={handleSubmit}>
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Product IDs:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter comma-separated Product IDs"
              placeholder="id1, id2, id3"
              value={productIds}
              onChange={(e) => setProductIds(e.target.value)}
              sx={formControlSx}
            />
            {parsedProductIds.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, color: "text.primary" }}>
                <strong>Total Product IDs:</strong> {parsedProductIds.length}
              </Typography>
            )}
          </Grid2>
        </Grid2>

        {/* Category */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Category:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="select"
              size="small"
              fullWidth
              label="Select Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              options={allowedDeviceCategories.map((cat) => ({
                value: cat,
                label: cat,
              }))}
              MenuProps={selectMenuProps}
              sx={formControlSx}
            />
          </Grid2>
        </Grid2>

        {/* Firmware Version */}
        <Grid2 container spacing={2} sx={{ mb: 2, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Firmware Version:</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <FormInput
              type="text"
              size="small"
              fullWidth
              label="Enter Firmware Version"
              value={fwVersion}
              onChange={(e) => setFwVersion(e.target.value)}
              sx={formControlSx}
            />
            {fwVersionError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                {fwVersionError}
              </Typography>
            )}
          </Grid2>
        </Grid2>

        {/* CD File */}
        <Grid2 container spacing={2} sx={{ mb: 3, alignItems: "center" }}>
          <Grid2 size={{ xs: 12, md: 2 }}>
            <LabelText sx={{ fontWeight: 600 }}>Certificate (CD File):</LabelText>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 10 }} sx={{ minWidth: 0 }}>
            <Box sx={formControlSx}>
              <input
                ref={cdInputRef}
                type="file"
                accept=".pem,.crt,.cer,.der,.p7b,.p7c,.key,.txt"
                onChange={(e) => setCdFile(e.target.files[0])}
                style={{
                  width: "100%",
                  boxSizing: "border-box",
                  padding: 8,
                  border: "1px solid rgba(0,0,0,0.42)",
                  borderRadius: 4,
                }}
              />
            </Box>
          </Grid2>
        </Grid2>

        {/* Buttons */}
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <AppButton
            variant="primary"
            size="medium"
            type="submit"
            disabled={loading}
            loading={loading}
          >
            Upload CD
          </AppButton>
          <AppButton
            variant="ghost"
            size="medium"
            type="button"
            onClick={resetForm}
            disabled={loading}
          >
            Reset
          </AppButton>
        </Box>
      </form>
    </Paper>
  );
}

export default UploadCDForm;